import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { EmailGuard } from "../App";
import { AuthGuard } from "../App";
import { StandardURL } from "../App";

const Photo = () => {
  const [rentalId, setRentalId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [showAddPhotoModal, setShowAddPhotoModal] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [token, setToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    setToken(token);
  }, []);

  const handleInputChange = (e) => {
    setRentalId(e.target.value);
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") {
      fetchPhotos();
    }
  };

  const fetchPhotos = () => {
    if (!rentalId) {
      toast.error("Please enter a rental ID.");
      return;
    }

    axios
      .get(`${StandardURL}/iNyumba/photos/fetch/${rentalId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        setPhotos(response.data.response);
      })
      .catch((error) => {
        console.error("Error fetching photos:", error);
        toast.error("Error fetching photos.");
      });
  };

  const handleAddPhoto = () => {
    setShowAddPhotoModal(true);
  };

  const closeModal = () => {
    setShowAddPhotoModal(false);
  };

  const handlePhotoFileChange = (e) => {
    setPhotoFile(e.target.files[0]);
  };

  const addPhoto = () => {
    if (!photoFile) {
      toast.error("Please select a photo to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("image", photoFile);

    axios
      .post(
        `${StandardURL}/iNyumba/images/images/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        // Update photos state with the response URL
        const newPhoto = { photo_url: response.data.response };
        setPhotos([...photos, newPhoto]);
        closeModal();
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error uploading photo:", error);
      });
  };

  const handleDeletePhoto = (photoId) => {
    axios
      .delete(
        `${StandardURL}/iNyumba/photos/delete/${photoId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        // Remove the deleted photo from photos state
        setPhotos(photos.filter((photo) => photo.photo_id !== photoId));
      })
      .catch((error) => {
        toast.error(error.response.data.response);
        console.error("Error deleting photo:", error);
      });
  };

   return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl rounded-2xl bg-white p-8 shadow-xl">
            <h1 className="mb-6 text-3xl font-bold tracking-tight text-gray-900">
              Rental Photos
            </h1>

            {/* Input field and button group */}
            <div className="mb-8 flex items-center justify-center space-x-4">
              <input
                type="text"
                value={rentalId}
                onChange={handleInputChange}
                onKeyPress={handleEnterKeyPress}
                placeholder="Enter Rental ID and press Enter..."
                className="w-full max-w-lg rounded-lg border border-gray-300 px-4 py-3 text-gray-700 placeholder-gray-500 transition-shadow focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={fetchPhotos}
                className="transform rounded-full bg-blue-600 px-6 py-3 font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Fetch Photos
              </button>
            </div>

            {/* Photo gallery */}
            <div className="mt-8">
              {photos.length > 0 ? (
                <>
                  <h2 className="mb-4 text-xl font-semibold text-gray-800">Photo Gallery</h2>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {photos.map((photo) => (
                      <div
                        key={photo.photo_id}
                        className="group relative transform overflow-hidden rounded-xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl"
                      >
                        <img
                          src={photo.photo_url}
                          alt={`rental photo ${photo.photo_id}`}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        {/* Delete button positioned on the image */}
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                          <button
                            onClick={() => handleDeletePhoto(photo.photo_id)}
                            className="transform rounded-full bg-red-600 p-3 text-white transition-all duration-200 hover:scale-110 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center p-12 text-gray-500">
                  <p>No photos found. Please enter a Rental ID and click 'Fetch Photos'.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default Photo;
